<script setup lang="ts">
import { ref } from 'vue';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';

interface ApiCredentials {
  clientId: string;
  clientSecret: string;
}

withDefaults(
  defineProps<{
    value: boolean;
    credentials?: ApiCredentials | null;
  }>(),
  {
    credentials: null,
  }
);

const emit = defineEmits<{
  (event: 'update:value', value: boolean): void;
}>();

const snackbarManager = useSnackbarManager();
const showSecret = ref(false);

async function copyToClipboard(text: string, label: string): Promise<void> {
  await navigator.clipboard.writeText(text);
  snackbarManager.show({
    message: `${label} copied to clipboard`,
    color: 'success',
  });
}

function closeDialog(): void {
  showSecret.value = false;
  emit('update:value', false);
}
</script>

<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="700"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="value"
    @update:value="emit('update:value', $event)"
  >
    <VCard>
      <VCardTitle class="headline"> API Keys Created </VCardTitle>

      <VCardText>
        <VAlert class="mb-8" outlined type="warning">
          Save the credentials in a secure way now. <br />
          The secret will only be shown here once and never again.
        </VAlert>

        <div v-if="credentials">
          <div class="d-flex align-center mb-6">
            <div class="mr-3" style="min-width: 100px">Client ID:</div>
            <VTextField
              class="flex-grow-1"
              dense
              hide-details
              readonly
              :value="credentials.clientId"
            />
            <VTooltip bottom open-delay="300">
              <template #activator="{ on, attrs }">
                <VBtn
                  class="icons"
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="copyToClipboard(credentials.clientId, 'Client ID')"
                >
                  <VIcon small>mdi-content-copy</VIcon>
                </VBtn>
              </template>
              <span>Copy Client ID</span>
            </VTooltip>
          </div>

          <div class="d-flex align-center mb-6">
            <div class="mr-3" style="min-width: 100px">Client Secret:</div>
            <VTextField
              class="flex-grow-1"
              dense
              hide-details
              readonly
              :type="showSecret ? 'text' : 'password'"
              :value="
                showSecret
                  ? credentials.clientSecret
                  : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••'
              "
            />
            <div class="d-flex">
              <VTooltip bottom open-delay="300">
                <template #activator="{ on, attrs }">
                  <VBtn
                    class="icons"
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="showSecret = !showSecret"
                  >
                    <VIcon small>{{ showSecret ? 'mdi-eye-off' : 'mdi-eye' }}</VIcon>
                  </VBtn>
                </template>
                <span>{{ showSecret ? 'Hide' : 'Revel' }} Secret</span>
              </VTooltip>
              <VTooltip bottom open-delay="300">
                <template #activator="{ on, attrs }">
                  <VBtn
                    class="icons"
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="copyToClipboard(credentials.clientSecret, 'Client Secret')"
                  >
                    <VIcon small>mdi-content-copy</VIcon>
                  </VBtn>
                </template>
                <span>Copy Client Secret</span>
              </VTooltip>
            </div>
          </div>
        </div>
      </VCardText>

      <VCardActions class="justify-end">
        <VBtn color="primary" @click="closeDialog()"> Done </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.icon {
  opacity: 0.7;
}
.icon:hover {
  opacity: 1;
}
</style>
